import React from "react";
import { Link } from "react-router-dom";
import { Category } from "../types";
import { getEffectiveFeatureFlags } from "../utils/featureFlags";

interface CategoriesSectionProps {
  categories: Category[];
}

export const CategoriesSection: React.FC<CategoriesSectionProps> = ({
  categories,
}) => {
  // Get feature flags
  const featureFlags = getEffectiveFeatureFlags();

  // Filter out categories with no products, then sort by product count (descending - most products first)
  const sortedCategories = [...categories]
    .filter((category) => (category.productCount || 0) > 0)
    .sort((a, b) => {
      const countA = a.productCount || 0;
      const countB = b.productCount || 0;
      return countB - countA;
    });

  const categoryColors = [
    "bg-red-400",
    "bg-green-400",
    "bg-blue-400",
    "bg-pink-400",
    "bg-purple-400",
    "bg-orange-400",
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-warm-cream-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - More playful */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <span className="text-5xl mr-3 animate-bounce">🌈</span>
            <span
              className="text-4xl mr-2 animate-bounce-gentle"
              style={{ animationDelay: "0.2s" }}
            >
              🎨
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
              Explore Our Categories
            </h2>
            <span
              className="text-4xl ml-2 animate-bounce-gentle"
              style={{ animationDelay: "0.4s" }}
            >
              🎯
            </span>
            <span
              className="text-5xl ml-3 animate-bounce"
              style={{ animationDelay: "0.6s" }}
            >
              ✨
            </span>
          </div>
          <p className="text-lg font-semibold text-gray-700 max-w-2xl mx-auto">
            🎪 Find the perfect toy for every age and interest! 🎪
          </p>
        </div>

        {/* Categories Grid - Show only first 6 categories on homepage */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-stretch">
          {sortedCategories.slice(0, 6).map((category, index) => {
            const categoryCard = (
              <div className="h-full flex flex-col bg-white rounded-2xl shadow-lg transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-purple-300 hover:shadow-2xl group cursor-pointer hover-scale-playful">
                {/* Category Icon - More playful */}
                <div
                  className={`${
                    categoryColors[index % categoryColors.length]
                  } p-6 text-center flex-shrink-0 rounded-t-2xl group-hover:animate-bounce-gentle`}
                >
                  <div className="text-5xl mb-2 transform group-hover:scale-125 transition-transform duration-300">
                    {category.icon}
                  </div>
                </div>

                {/* Category Info - More playful */}
                <div className="p-4 text-center flex flex-col flex-grow bg-gradient-to-b from-white to-purple-50/30">
                  <h3 className="font-bold text-gray-900 mb-1 transition-colors duration-200 group-hover:text-purple-600 text-lg">
                    {category.name}
                  </h3>
                  <p className="text-sm font-semibold text-purple-600 mb-2 flex-shrink-0">
                    🎁 {category.productCount}{" "}
                    {category.productCount === 1 ? "item" : "items"}
                  </p>
                  <div className="text-xs text-gray-600 flex-grow font-medium">
                    {category.description}
                  </div>
                </div>
              </div>
            );

            // Always make categories clickable - link to category page
            return (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="h-full group cursor-pointer transform hover:scale-105 transition-all duration-300"
              >
                {categoryCard}
              </Link>
            );
          })}
        </div>

        {/* View All Categories Button */}

        <div className="text-center mt-12">
          <Link
            to="/categories"
            className="bg-white border-2 border-soft-purple-300 text-soft-purple-600 hover:bg-soft-purple-50 font-semibold py-3 px-8 rounded-full shadow-md transform hover:scale-105 transition-all duration-200 inline-block"
          >
            View All Categories ({categories.length})
          </Link>
        </div>

        {/* View All Products Button - Only show if products feature is enabled */}
        {featureFlags.showProducts && (
          <div className="text-center mt-6">
            <Link
              to="/products"
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-4 px-10 rounded-full shadow-xl hover:shadow-2xl transform hover:scale-110 hover-bounce transition-all duration-300 inline-block"
            >
              🛍️ View All Products 🛍️
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CategoriesSection;
